import { Dispatch, SetStateAction, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { GetApiIntroducerPortalDirectorSearchResponse } from '@iwoca/lapi-client/introducer_portal';
import { Label, Message, Tooltip } from '@iwoca/orion';
import { SearchableDropdown } from 'components/SearchableDropdown/SearchableDropdown';
import { fetchGetCompanySearch } from 'lendingApi/lendingApi';
import styles from './CompanyInputUK.module.css';

type CompanySearchCompany = GetCompanySearchResponse['data'][number];
type TProps = {
  setSelectedCompany: Dispatch<
    SetStateAction<GetCompanySearchResponse['data'][number] | undefined>
  >;
  setSelectedPerson?: (
    person:
      | GetApiIntroducerPortalDirectorSearchResponse['data'][number]
      | undefined,
  ) => void;
  onChange: (company: CompanySearchCompany) => void;
  tooltip?: string;
  error?: string;
  disabled?: boolean;
};

export const CompanyInputUK = ({
  onChange,
  setSelectedCompany,
  setSelectedPerson,
  error,
  disabled,
  tooltip = 'Companies house searches both current and previous company names. If you cannot find the business you are looking for try using the company number.',
}: TProps) => {
  const [hasError, setHasError] = useState(false);

  const getCompanies = async (searchQuery: string) => {
    setHasError(false);
    try {
      const response = await fetchGetCompanySearch({
        companyName: searchQuery,
      });
      return response.data;
    } catch {
      unset();
      setHasError(true);
      return [];
    }
  };

  const unset = () => {
    setSelectedCompany(undefined);
    setSelectedPerson?.(undefined);
  };

  return (
    <>
      <SearchableDropdown
        onChange={onChange}
        optionsGetter={getCompanies}
        optionRenderer={(company) => `${company.name} | ${company.number}`}
        valueRenderer={(company) => `${company.name} | ${company.number}`}
        placeholder="Search for a company name or number..."
        onClear={unset}
        disabled={disabled}
        labelComponent={
          <>
            <Combobox.Label>
              <Label>Company name or number</Label>
            </Combobox.Label>
            <Tooltip dataTestId="company-search-tooltip" content={tooltip} />
          </>
        }
      />

      {(hasError || error) && (
        <Message
          className={styles.error}
          message={
            error ||
            'Unable to retrieve company details. Please try again later.'
          }
          type="error"
        />
      )}
    </>
  );
};
